"use client";

import { Component, useCallback, useEffect, useState } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface RiveSafetyBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface RiveSafetyBoundaryState {
  hasError: boolean;
}

export class RiveSafetyBoundary extends Component<
  RiveSafetyBoundaryProps,
  RiveSafetyBoundaryState
> {
  override state: RiveSafetyBoundaryState = { hasError: false };

  static getDerivedStateFromError(): RiveSafetyBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.onError?.(error, errorInfo);
  }

  override render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

export function useRiveLoadFailure(onFailure?: () => void) {
  const [loadFailed, setLoadFailed] = useState(false);
  const handleFailure = useCallback(() => {
    setLoadFailed(true);
    onFailure?.();
  }, [onFailure]);
  const handleLoadError = useCallback(() => {
    handleFailure();
  }, [handleFailure]);
  const handleContextLost = handleFailure;

  return { loadFailed, handleLoadError, handleContextLost };
}

interface RiveCleanupTarget {
  cleanup: () => void;
}

export function useRiveWebGLContextGuard(
  canvas: HTMLCanvasElement | null,
  rive: RiveCleanupTarget | null,
  onContextLost: () => void
) {
  useEffect(() => {
    if (!canvas) return;

    const handleContextLost = (event: Event) => {
      event.preventDefault();
      rive?.cleanup();
      onContextLost();
    };

    canvas.addEventListener("webglcontextlost", handleContextLost);
    return () => {
      canvas.removeEventListener("webglcontextlost", handleContextLost);
    };
  }, [canvas, rive, onContextLost]);
}

export function useRiveCleanup(rive: RiveCleanupTarget | null) {
  useEffect(() => {
    return () => {
      rive?.cleanup();
    };
  }, [rive]);
}
